import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, alpha, useTheme,
} from '@mui/material';
import {
  Close as CloseIcon, Download as DownloadIcon, Upload as UploadIcon,
  CheckCircle as CheckIcon, Error as ErrorIcon, Warning as WarningIcon,
} from '@mui/icons-material';
import wineListSyncService, {
  type WineListSyncPreviewResponse, type WineListSyncCommitResponse,
  type WineListSyncPreviewRow, type WineListSyncError,
} from '../../services/wineListSyncService';
import wineListFormatService from '../../services/wineListFormatService';
import {
  downloadWineListSyncTemplate, downloadExistingItemsAsImport,
} from '../../utils/wineListSyncTemplate';
import {
  downloadImportPreviewReport, joinIssues, type ReportColumn,
} from '../../utils/importPreviewReport';
import type { WineListFormat } from '../../types/venue';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  venueId: string;
  wineListId: string;
}

type Phase = 'idle' | 'previewing' | 'previewed' | 'committing' | 'committed';

/**
 * W8 — wine list item sync dialog.
 *
 * Mirrors the inventory import dialog (preview → confirm wizard) so admins
 * encounter the same UX for both modules. Handles export, template, preview,
 * and commit. Section auto-create and format-price upserts happen on commit.
 */
const WineListSyncDialog: React.FC<Props> = ({ open, onClose, onSuccess, venueId, wineListId }) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<WineListSyncError | null>(null);
  const [preview, setPreview] = useState<WineListSyncPreviewResponse | null>(null);
  const [commit, setCommit] = useState<WineListSyncCommitResponse | null>(null);
  const [formats, setFormats] = useState<WineListFormat[]>([]);
  const [exportLoading, setExportLoading] = useState(false);
  // Preview-table filter chips: 'all' shows everything, others narrow by action.
  // Download Report always emits ALL preview rows regardless of filter.
  const [previewFilter, setPreviewFilter] = useState<'all' | 'CREATE' | 'UPDATE' | 'ERROR'>('all');

  // Reset state every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setFile(null);
    setPhase('idle');
    setError(null);
    setPreview(null);
    setCommit(null);
    setPreviewFilter('all');
    wineListFormatService.getFormats(venueId, wineListId)
      .then(setFormats)
      .catch(() => { /* non-fatal — template will just lack format columns */ });
  }, [open, venueId, wineListId]);

  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (f) {
      setFile(f);
      setError(null);
      setPreview(null);
      setCommit(null);
      setPhase('idle');
    }
  };

  const handleDownloadTemplate = () => downloadWineListSyncTemplate(formats);

  const handleDownloadExisting = async () => {
    setExportLoading(true);
    try {
      const exp = await wineListSyncService.exportItems(venueId, wineListId);
      downloadExistingItemsAsImport(exp, formats);
    } catch (err: any) {
      setError({ code: 'EXPORT_FAILED', message: err?.message ?? 'Could not export items.' });
    } finally {
      setExportLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!file) return;
    setPhase('previewing');
    setError(null);
    try {
      const result = await wineListSyncService.preview(venueId, wineListId, file);
      setPreview(result);
      setPhase('previewed');
    } catch (err: any) {
      setError(err as WineListSyncError);
      setPhase('idle');
    }
  };

  /** Download the FULL preview as XLSX. Always emits all rows so the user
   *  can fix the source file outside the in-dialog window. */
  const handleDownloadPreviewReport = () => {
    if (!preview) return;
    const cols: ReportColumn<WineListSyncPreviewRow>[] = [
      { header: 'Row Number', value: r => r.rowNumber },
      { header: 'Action',     value: r => r.action },
      { header: 'WineListItem ID', value: r => r.data.wineListItemId },
      { header: 'Wine ID',    value: r => r.data.wineId },
      { header: 'Wine Name',  value: r => r.data.wineName },
      { header: 'Producer',   value: r => r.data.producer },
      { header: 'Vintage',    value: r => r.data.vintage },
      { header: 'Section',    value: r => r.data.section },
      { header: 'Section will be created?', value: r => r.resolved.sectionWillBeCreated ? 'yes' : '' },
      { header: 'Resolved Wine ID', value: r => r.resolved.wineId ?? '' },
      { header: 'Existing Item ID', value: r => r.resolved.existingItemId ?? '' },
      { header: 'Errors',     value: r => joinIssues(r.errors) },
      { header: 'Warnings',   value: r => joinIssues(r.warnings) },
    ];
    downloadImportPreviewReport('wine-list-import-preview-report.xlsx', cols, preview.rows);
  };

  const handleConfirm = async () => {
    if (!file) return;
    setPhase('committing');
    setError(null);
    try {
      const result = await wineListSyncService.commit(venueId, wineListId, file);
      setCommit(result);
      setPhase('committed');
    } catch (err: any) {
      setError(err as WineListSyncError);
      setPhase('previewed');
    }
  };

  const handleClose = () => {
    if (phase === 'previewing' || phase === 'committing') return;
    if (phase === 'committed' && commit && commit.summary.created + commit.summary.updated > 0) {
      onSuccess();
    }
    onClose();
  };

  const importableRowCount = preview ? preview.rows.filter(r => r.action !== 'ERROR').length : 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>Import Wine List Items</Typography>
        <IconButton size="small" onClick={handleClose} disabled={phase === 'previewing' || phase === 'committing'}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: 1 }}>
            <Typography variant="body2" fontWeight={600}>{error.code}</Typography>
            <Typography variant="body2">{error.message}</Typography>
            {error.missing && error.missing.length > 0 && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace' }}>
                Missing: {error.missing.join(', ')}
              </Typography>
            )}
          </Alert>
        )}

        {/* IDLE — file picker + downloads */}
        {(phase === 'idle' || phase === 'previewing') && (
          <Stack spacing={2.5}>
            {/* Download row */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Get the template or current items
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Template starts with one example row in row 2 — replace it with your own data. The current export
                includes IDs so you can edit and re-upload.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadTemplate}
                  disabled={phase === 'previewing'}
                  sx={{ textTransform: 'none' }}
                >
                  Download template
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={exportLoading ? <CircularProgress size={14} /> : <DownloadIcon />}
                  onClick={handleDownloadExisting}
                  disabled={exportLoading || phase === 'previewing'}
                  sx={{ textTransform: 'none' }}
                >
                  Download existing items
                </Button>
              </Box>
            </Paper>

            {/* File chooser */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Upload .xlsx file
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UploadIcon />}
                  onClick={handlePickFile}
                  disabled={phase === 'previewing'}
                  sx={{ textTransform: 'none' }}
                >
                  Choose file
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {file ? file.name : 'No file selected'}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handlePreview}
                  disabled={!file || phase === 'previewing'}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {phase === 'previewing' ? <CircularProgress size={16} color="inherit" /> : 'Preview import'}
                </Button>
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </Paper>
          </Stack>
        )}

        {/* PREVIEWED — summary + per-row table */}
        {phase === 'previewed' && preview && (
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {file?.name}
              </Typography>
              <Button
                size="small"
                onClick={() => { setFile(null); setPreview(null); setPhase('idle'); }}
                sx={{ textTransform: 'none' }}
              >
                Choose another file
              </Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Chip label={`${preview.summary.create} to create`} color="primary" variant="outlined" size="small" />
              <Chip label={`${preview.summary.update} to update`} color="success" variant="outlined" size="small" />
              <Chip label={`${preview.summary.error} with errors`} color={preview.summary.error > 0 ? 'error' : 'default'} variant="outlined" size="small" />
              <Chip label={`${preview.totalRows} total rows`} size="small" />
            </Box>

            {preview.summary.error > 0 && (
              <Alert severity="warning" sx={{ borderRadius: 1 }}>
                Rows with errors will be skipped on confirm. Fix and re-upload to import them.
              </Alert>
            )}

            {/* Filter chips + Download report button.
                Filter narrows the in-dialog table only; the report download
                always emits the full preview so the user can fix the file. */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {([
                  { key: 'all',    label: `All (${preview.totalRows})` },
                  { key: 'ERROR',  label: `Errors (${preview.summary.error})` },
                  { key: 'CREATE', label: `Created (${preview.summary.create})` },
                  { key: 'UPDATE', label: `Updated (${preview.summary.update})` },
                ] as const).map(chip => (
                  <Chip
                    key={chip.key}
                    label={chip.label}
                    size="small"
                    variant={previewFilter === chip.key ? 'filled' : 'outlined'}
                    color={previewFilter === chip.key ? 'primary' : 'default'}
                    onClick={() => setPreviewFilter(chip.key)}
                    sx={{ fontSize: '0.72rem', height: 24, fontWeight: 600 }}
                  />
                ))}
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadPreviewReport}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Download report
              </Button>
            </Box>

            <TableContainer sx={{ maxHeight: 360, border: `1px solid ${alpha(theme.palette.divider, 0.4)}`, borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={thStyle}>Row</TableCell>
                    <TableCell sx={thStyle}>Action</TableCell>
                    <TableCell sx={thStyle}>Wine</TableCell>
                    <TableCell sx={thStyle}>Section</TableCell>
                    <TableCell sx={thStyle}>Issues</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(previewFilter === 'all'
                    ? preview.rows
                    : preview.rows.filter(r => r.action === previewFilter)
                  ).map(r => (
                    <TableRow key={r.rowNumber}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.rowNumber}</TableCell>
                      <TableCell>
                        <ActionChip action={r.action} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>
                        {r.data.wineName || r.data.wineId || r.data.wineListItemId || '—'}
                        {r.data.vintage && (
                          <Box component="span" sx={{ ml: 0.75, color: 'text.secondary', fontStyle: 'italic' }}>{r.data.vintage}</Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>
                        {r.data.section || <em style={{ color: theme.palette.text.disabled }}>none</em>}
                        {r.resolved.sectionWillBeCreated && (
                          <Chip label="will create" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} color="warning" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <IssuesCell errors={r.errors} warnings={r.warnings} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}

        {/* COMMITTING / COMMITTED */}
        {phase === 'committing' && (
          <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">Importing items…</Typography>
          </Box>
        )}

        {phase === 'committed' && commit && (
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Chip label={`${commit.summary.created} created`} color="success" variant="filled" size="small" />
              <Chip label={`${commit.summary.updated} updated`} color="primary" variant="filled" size="small" />
              {commit.summary.errored > 0 && <Chip label={`${commit.summary.errored} errored`} color="error" variant="filled" size="small" />}
              {commit.summary.skipped > 0 && <Chip label={`${commit.summary.skipped} skipped`} color="default" variant="outlined" size="small" />}
            </Box>
            <TableContainer sx={{ maxHeight: 360, border: `1px solid ${alpha(theme.palette.divider, 0.4)}`, borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={thStyle}>Row</TableCell>
                    <TableCell sx={thStyle}>Status</TableCell>
                    <TableCell sx={thStyle}>Action</TableCell>
                    <TableCell sx={thStyle}>Result</TableCell>
                    <TableCell sx={thStyle}>Errors</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {commit.rows.map(r => (
                    <TableRow key={r.rowNumber}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.rowNumber}</TableCell>
                      <TableCell>
                        <StatusChip status={r.status} />
                      </TableCell>
                      <TableCell>
                        <ActionChip action={r.action} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                        {r.result.itemId && <span style={{ fontFamily: 'monospace' }}>{r.result.itemId.slice(-6)}</span>}
                        {r.result.sectionCreated && <Chip label="section +" size="small" sx={{ ml: 0.75, height: 18, fontSize: '0.65rem' }} color="warning" variant="outlined" />}
                        {r.result.formatPricesUpdated > 0 && <Chip label={`${r.result.formatPricesUpdated} formats`} size="small" sx={{ ml: 0.75, height: 18, fontSize: '0.65rem' }} variant="outlined" />}
                      </TableCell>
                      <TableCell>
                        <IssuesCell errors={r.errors} warnings={r.warnings} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {phase === 'previewed' && preview && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={importableRowCount === 0}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Confirm import ({importableRowCount} {importableRowCount === 1 ? 'row' : 'rows'})
          </Button>
        )}
        {phase === 'committed' && (
          <Button variant="contained" onClick={handleClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Done
          </Button>
        )}
        {(phase === 'idle' || phase === 'previewed') && (
          <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ── Subcomponents ─────────────────────────────────────────────────────────────

const thStyle = {
  fontWeight: 600,
  fontSize: '0.72rem',
  textTransform: 'uppercase' as const,
  color: 'text.secondary',
};

const ActionChip: React.FC<{ action: 'CREATE' | 'UPDATE' | 'ERROR' }> = ({ action }) => {
  const config: Record<string, { label: string; color: 'primary' | 'success' | 'error' }> = {
    CREATE: { label: 'Create', color: 'primary' },
    UPDATE: { label: 'Update', color: 'success' },
    ERROR: { label: 'Error', color: 'error' },
  };
  const c = config[action];
  return <Chip label={c.label} color={c.color} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />;
};

const StatusChip: React.FC<{ status: 'SUCCESS' | 'ERROR' | 'SKIPPED' }> = ({ status }) => {
  const config: Record<string, { label: string; color: 'success' | 'error' | 'default' }> = {
    SUCCESS: { label: 'Success', color: 'success' },
    ERROR: { label: 'Error', color: 'error' },
    SKIPPED: { label: 'Skipped', color: 'default' },
  };
  const c = config[status];
  return <Chip label={c.label} color={c.color} size="small" sx={{ height: 22, fontSize: '0.7rem' }} />;
};

const IssuesCell: React.FC<{ errors: string[]; warnings: string[] }> = ({ errors, warnings }) => {
  if (errors.length === 0 && warnings.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
        <CheckIcon fontSize="small" />
        <Typography variant="caption">OK</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {errors.map((e, i) => (
        <Box key={`e${i}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, color: 'error.main' }}>
          <ErrorIcon fontSize="small" sx={{ fontSize: '0.95rem', mt: '2px' }} />
          <Typography variant="caption">{e}</Typography>
        </Box>
      ))}
      {warnings.map((w, i) => (
        <Box key={`w${i}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, color: 'warning.main' }}>
          <WarningIcon fontSize="small" sx={{ fontSize: '0.95rem', mt: '2px' }} />
          <Typography variant="caption">{w}</Typography>
        </Box>
      ))}
    </Box>
  );
};

export default WineListSyncDialog;
